import { ValidationService } from 'aspnet-client-validation';
import { querySelectorLast } from "../utils/dom";

/**
 * Valdiation form custom element
 */
export default class FormValidation extends HTMLFormElement {

    validationInputCssClassName = "input-validation-error";
    validationMessageCssClassName = "field-validation-error";
    validationSummaryCssClassName = "validation-summary-errors";
    validationMessageIdSuffix = "-error-msg";

    formGroupClassName = "form-group";
    formGroupInvalidClassName = "invalid-group";
    handleEnterPressSelector = "input:not([type=button]):not([type=submit]):not(button)"

    validationService = null;

    connectedCallback() {

        // Load Umbraco Forms script that handles logic if this is an umbraco form
        if (this.isUmbracoForm) {
            window.aspnetValidation = { ValidationService };
            import(/* webpackChunkName: "umbracoforms" */ "../umbraco/umbracoforms").then(() => {
                this.validationService = window["umbracoFormsValidationService"] || new ValidationService();
                this.initForm();
            });
        }
        else {
            this.validationService = new ValidationService();
            this.initForm();
        }

    }

    disconnectedCallback() {
        this.removeEventListener("validation", this.invalidHandler);
        this.validationSummaryObserver?.disconnect();
    }

    initForm() {

        const form = this;

        this.validationService.focusFirstInvalid = false;
        this.validationService.ValidationInputCssClassName = this.validationInputCssClassName;
        this.validationService.ValidationSummaryCssClassName = this.validationSummaryCssClassName;
        this.validationService.ValidationMessageCssClassName = this.validationMessageCssClassName;

        this.validationService.bootstrap();

        // Handle validation on submit
        this.addEventListener("validation", this.invalidHandler);

        this.validationSummary?.setAttribute("tabindex", "-1");

        // Add pattern attribute if missing
        this.querySelectorAll("[data-val-regex-pattern]").forEach(el => {

            if (el.hasAttribute("pattern"))
                return;

            el.setAttribute("pattern", el.getAttribute("data-val-regex-pattern"));
        });

        // Handle aria-attributes
        this.validationFields.forEach(field => {
            if (field.classList.contains(this.validationInputCssClassName) && !field.getAttribute("aria-invalid")) {
                field.setAttribute("aria-invalid", "true");
            }

            const errorMsg = this.getErrorMsgForField(field);

            if (errorMsg) {
                errorMsg.id = field.id + this.validationMessageIdSuffix;
            }
        });

        // Watch for changes to the validation summary and update the error count
        if (this.validationSummary) {
            this.validationSummaryObserver = new MutationObserver((mutations) => {
                mutations.forEach(mutation => {
                    if (mutation.addedNodes.length >= 1 && Array.from(mutation.addedNodes).some(node => node.tagName == "UL")) {
                        form.updateValidationSummaryErrorCount();
                    }
                });
            });

            this.validationSummaryObserver.observe(this.validationSummary, {
                childList: true,
            });
        }

        // Watch for changes of classes on fields to detect when field is valid or invalid
        this.fieldObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                this.fieldValidationHandler(mutation.target);
            });
        });

        this.validationFields.forEach(field => {
            this.fieldObserver.observe(field,
                {
                    attributeFilter: ["class"]
                });
        });

        if (this.dataset.focusonerror === "true") {
            this.scrollIntoView();
            this.validationSummary.focus();
        }
    }

    invalidHandler = () => {

        const form = this;
        const validationSummary = this.validationSummary;

        // Set focus on first error field or on validation summary if it exists
        if (validationSummary === null) {

            window.setTimeout(() => {
                this.querySelector(`[data-val="true"].${this.validationInputCssClassName}`)?.focus();
            }, 500);

        }
        else {
            const invalidElements = form.querySelectorAll(`[data-val="true"].${this.validationInputCssClassName}`),
                errorCount = invalidElements != null ? invalidElements.length : 0;

            if (errorCount) {

                form.scrollIntoView(true);

                window.setTimeout(() => {
                    validationSummary.focus();
                }, 250);

            }
        }

    }

    fieldValidationHandler = (field) => {
        const valid = !field.classList.contains(this.validationInputCssClassName);

        if (valid) {
            field.removeAttribute("aria-invalid");
            field.removeAttribute("aria-describedby");
        }
        else {
            field.setAttribute("aria-invalid", "true");
            const errorMsg = this.getErrorMsgForField(field);

            if (errorMsg)
                field.setAttribute("aria-describedby", errorMsg.id);
        }
        
    }

    updateValidationSummaryErrorCount = () => {

        if (this.validationSummary == null)
            return;

        const heading = this.validationSummary.firstElementChild;

        if (heading == null || heading.tagName === "UL")
            return;

        const errorCount = this.validationSummary.querySelectorAll("li").length;

        heading.innerText = heading.innerText.replace(/[0-9]/, errorCount);

    }

    handleEnterPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const submitBtn = querySelectorLast("[type=submit]", this);
            submitBtn.click();
        }
    }

    getErrorMsgForField(field) {
        return this.querySelector(`[data-valmsg-for="${field.id}"]`);
    }

    get validationFields() {
        return this.querySelectorAll("[data-val=true]");
    }

    get validationSummary() {
        return this.querySelector("[data-valmsg-summary]");
    }

    get isUmbracoForm() {
        return this.closest(".umbraco-forms-form") != null;
    }
}

customElements.define('form-validation', FormValidation, { extends: 'form' });